import { forwardRef, useEffect, useRef, useState } from 'react'
import WarningIcon from '~/components/icons/Warning'
import { classNames } from '~/helpers/string'

// Maybe in config file
export type FieldProps = {
  label: string
  error?: boolean
  optional?: boolean
  helperText?: string | React.ReactNode
  disabled?: boolean
  inputClassName?: string
}

export interface TextFieldProps extends React.ComponentProps<'input'>, FieldProps {
  wrapperClassName?: string
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    id,
    label,
    optional,
    error,
    disabled,
    helperText = '',
    wrapperClassName = '',
    className = '',
    startAdornment = null,
    endAdornment = null,
    ...restProps
  } = props
  const startAdornmentRef = useRef<HTMLDivElement | null>(null)
  const endAdornmentRef = useRef<HTMLDivElement | null>(null)
  const [adornmentWidth, setAdornmentWidth] = useState({ start: 0, end: 0 })

  useEffect(() => {
    // Determine the width of the adornments to correctly align the text inside input
    setAdornmentWidth((prev) => ({
      start: startAdornmentRef.current?.offsetWidth ?? prev.start,
      end: endAdornmentRef.current?.offsetWidth ?? prev.end,
    }))
  }, [])

  const getInputWidth = (hasError: boolean, adornmentWidth: number): number => {
    if (adornmentWidth) return adornmentWidth + 24
    if (hasError) return 14
    return 15
  }

  return (
    <div className={classNames('relative', wrapperClassName)}>
      <input
        ref={ref}
        id={id}
        disabled={disabled}
        className={classNames(
          `peer block h-[46px] w-full rounded-md bg-transparent pb-[13px] pt-[15px] text-sm font-normal focus:outline-none focus:ring-0 disabled:pointer-events-none disabled:text-brand-violet-950/50 disabled:opacity-60`,
          error
            ? 'border-2 border-brand-red-700  focus:border-2 focus:border-brand-red-700 '
            : 'border-1 border-brand-neutral-200 duration-100 hover:border-brand-neutral-500 focus:border-transparent focus:shadow-[0_0_0_2px_#671ddb] ',
          className,
        )}
        style={{
          ...restProps.style,
          paddingLeft: getInputWidth(!!error, adornmentWidth.start),
          paddingRight: getInputWidth(!!error, adornmentWidth.end),
        }}
        {...restProps}
      />
      {startAdornment && (
        <div
          ref={startAdornmentRef}
          className={classNames(
            `absolute left-4 top-[23px] -translate-y-1/2 transform`,
            disabled ? 'pointer-events-none opacity-35' : '',
          )}
        >
          {startAdornment}
        </div>
      )}
      {/* Don't render endAdornment if there is error icon rendered (in error state) */}
      {endAdornment && (
        <div
          ref={endAdornmentRef}
          className={classNames(
            `absolute right-3 top-[23px] flex -translate-y-1/2 transform flex-row items-center gap-2`,
            disabled ? 'pointer-events-none opacity-35' : '',
          )}
        >
          {endAdornment}
          {error && <WarningIcon width="20" height="20" className="text-brand-red-700" />}
        </div>
      )}
      <label
        htmlFor={id}
        className={classNames(
          `z-2 absolute left-3  top-2 -translate-y-4 transform bg-white px-1 text-xs duration-100`,
          error
            ? 'text-brand-red-700 focus:outline-brand-red-700'
            : 'text-brand-gray-500 peer-focus:text-brand-purple-700',
          disabled ? 'pointer-events-none text-brand-violet-950 text-opacity-35' : '',
        )}
      >
        {label} {!optional && <span className="asterisk !text-inherit"></span>}
      </label>
      {props.type !== 'number' && error && !endAdornment && (
        <span
          className={classNames('absolute right-3 top-[23px] -translate-y-1/2 transform', disabled ? 'opacity-35' : '')}
        >
          <WarningIcon width="20" height="20" className="text-brand-red-700" />
        </span>
      )}
      {helperText && (
        <span
          className={classNames(
            'ml-4 mt-1 text-xs font-normal',
            error ? ' text-brand-red-700' : 'mt-[5px] text-brand-gray-500',
            disabled ? 'text-brand-violet-950 text-opacity-35' : '',
          )}
        >
          {helperText}
        </span>
      )}
    </div>
  )
})

TextField.displayName = 'TextField'

export default TextField
